import { defaultDateFormat } from "apps/visits/core/config";
import { Form } from "apps/visits/packages/ui/src/components";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import InputMask from "react-input-mask";
import { Checkbox } from "@/core/shared/v2/ui/checkbox";
import { Input } from "@/core/shared/v2/ui/input";
import { Label } from "@/core/shared/v2/ui/label";
import type { FormValues } from "../../user-data-form/validation-schema";

type PeselSectionProps = {
  coreRequired: boolean;
  noPesel?: boolean;
  orderType: FormValues["orderType"];
};

export const PeselSection = ({ coreRequired, noPesel, orderType }: PeselSectionProps) => {
  const { t } = useTranslation();
  const { control, register, errors } = useFormContext<FormValues>();

  return (
    <>
      <div className="fade-in slide-in-from-top-2 animate-in fill-mode-both duration-300">
        <Controller
          control={control}
          name="noPesel"
          render={({ onChange, value, ref }) => (
            <div className="flex items-center gap-2">
              <Checkbox ref={ref} id="noPesel" name="noPesel" checked={value} onCheckedChange={onChange} />
              <div className="flex flex-col">
                <Form.Label htmlFor="noPesel" className="-ml-2 pt-1.5 pb-0 font-normal text-foreground">
                  <span className="text-sm">
                    {orderType === "for_someone"
                      ? t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.PATIENT_NO_PESEL")
                      : t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.NO_PESEL")}
                  </span>
                </Form.Label>
                {errors.noPesel?.message && (
                  <p className="font-medium text-destructive text-sm">{errors.noPesel.message}</p>
                )}
              </div>
            </div>
          )}
        />
      </div>
      {!noPesel && (
        <div className="fade-in slide-in-from-top-2 mt-2 flex animate-in flex-col gap-2 fill-mode-both duration-300">
          <Label htmlFor="pesel">
            {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.PESEL")}
            {coreRequired ? " *" : ""}
          </Label>

          <Input ref={register} name="pesel" required={coreRequired} aria-invalid={!!errors.pesel?.message} />
        </div>
      )}
      {!noPesel && errors.pesel?.message && (
        <p className="-mt-2 font-medium text-destructive text-sm">{errors.pesel.message}</p>
      )}
      {noPesel && (
        <div className="fade-in slide-in-from-top-2 mt-2 flex animate-in flex-col gap-5 fill-mode-both duration-300 lg:flex-row">
          <div className="lg:w-1/2">
            <Controller
              control={control}
              name="dateOfBirth"
              defaultValue=""
              render={({ onChange, value, onBlur }) => (
                <InputMask
                  mask="99.99.9999"
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  maskPlaceholder={defaultDateFormat}
                  placeholder={defaultDateFormat}
                >
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="dateOfBirth">
                      {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.DATE_OF_BIRTH")}
                      {coreRequired ? " *" : ""}
                    </Label>
                    <Input aria-invalid={!!errors.dateOfBirth?.message} required={coreRequired} inputMode="numeric" />
                  </div>
                </InputMask>
              )}
            />
            {errors.dateOfBirth?.message && (
              <p className="mt-2 font-medium text-destructive text-sm">{errors.dateOfBirth.message}</p>
            )}
          </div>
          <div className="relative">
            <Form.Label
              title={`${t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.BIOLOGICAL_SEX")}${coreRequired ? " *" : ""}`}
              className="pt-0"
            />
            <div className="flex gap-3">
              <Form.InputRadio
                ref={register}
                name="gender"
                value="female"
                label={t("COMMONS.GENERAL.BIOLOGICAL_SEX.FEMALE")}
              />
              <Form.InputRadio
                ref={register}
                name="gender"
                value="male"
                label={t("COMMONS.GENERAL.BIOLOGICAL_SEX.MALE")}
              />
              {errors.gender && <span className="absolute -bottom-5 text-error text-xs">{errors.gender.message}</span>}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
